import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiInbox, FiCheckCircle, FiMail, FiRefreshCw, FiExternalLink,
  FiFilter, FiLoader, FiClock, FiUser, FiSend, FiEye, FiX,
} from 'react-icons/fi';

// Mirror of the Edge Function's bodyToHtml() — blank lines split
// paragraphs, single line breaks become <br>. Keep in sync with
// supabase/functions/send-feedback-reply/index.ts so the preview
// matches exactly what the user receives.
const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
   .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const bodyToHtml = (text: string) =>
  text
    .split(/\n\s*\n/)
    .map((para) => {
      const escaped = escapeHtml(para.trim()).replace(/\n/g, '<br>');
      return `<p style="margin:0 0 14px 0;">${escaped}</p>`;
    })
    .join('');
import { supabase } from '@/integrations/supabase/client';

/**
 * Admin panel — all replies to "why did you leave?" and other broadcast
 * feedback prompts, in one place. Sourced from the growth_feedback
 * table via admin_list_growth_feedback().
 *
 * Read a message → optionally paste it into gmail to reply → hit
 * "Mark responded" (with optional notes) so the queue drains.
 */

interface Feedback {
  id: string;
  user_email: string;
  first_name: string | null;
  message: string;
  source: string;
  admin_notes: string | null;
  responded_at: string | null;
  created_at: string;
  admin_reply_text?: string | null;
  pro_granted_at?: string | null;
  pro_granted_until?: string | null;
}

// One-liner default that repliers can tweak before sending. Ties the
// reply back to the original email so it threads in gmail.
const defaultSubject = 'Re: your feedback on Snap Chart AI';
const defaultReplyBody = (name: string, quote: string) =>
  `Hi ${name},\n\nThanks so much for taking the time to reply — this is exactly the kind of feedback I needed.\n\n${quote ? `You said: "${quote}"\n\n` : ''}[Your personal response here]\n\nAs promised, I've unlocked 3 months of Pro on your account. No card needed, just log back in.\n\nIf Pro ends up being worth it after your 3 free months, you can lock in the Founding Member 40%-off-for-life deal here: https://snap-chart.ai/founding\n\n— JM`;


const GrowthFeedbackPanel: React.FC = () => {
  const [list, setList] = useState<Feedback[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('unread');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  // In-app reply composer state — per-feedback so multiple can be open
  // at once without losing drafts.
  const [replySubject, setReplySubject] = useState<Record<string, string>>({});
  const [replyBody, setReplyBody] = useState<Record<string, string>>({});
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<Record<string, { ok: boolean; text: string }>>({});
  // Preview modal — shows the reply rendered exactly as the recipient
  // will see it in their inbox.
  const [previewFor, setPreviewFor] = useState<Feedback | null>(null);
  const [grantingId, setGrantingId] = useState<string | null>(null);
  const [grantResult, setGrantResult] = useState<Record<string, { ok: boolean; text: string }>>({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await supabase.rpc('admin_list_growth_feedback', {
        p_limit: 500,
        p_unread_only: filter === 'unread',
      });
      if ((data as any)?.success) {
        setList(((data as any).feedback || []) as Feedback[]);
        setTotalCount((data as any).total_count || 0);
        setUnreadCount((data as any).unread_count || 0);
      }
    } catch (err) {
      console.error('[feedback] load failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const markResponded = async (id: string) => {
    setSavingId(id);
    try {
      await supabase.rpc('admin_mark_feedback_responded', {
        p_id: id,
        p_notes: notesDraft[id] || null,
      });
      load();
      setExpanded(null);
    } finally {
      setSavingId(null);
    }
  };

  const grantPro = async (f: Feedback, months: number = 3) => {
    if (!confirm(`Grant ${months} months of Pro Trader to ${f.user_email}?\n\nTheir account will be upgraded immediately.`)) return;
    setGrantingId(f.id);
    setGrantResult(prev => ({ ...prev, [f.id]: { ok: true, text: 'Granting…' } }));
    try {
      const { data, error } = await supabase.rpc('admin_grant_reply_trial', {
        p_feedback_id: f.id,
        p_months: months,
      });
      if (error) throw error;
      if (!data?.success) {
        const map: Record<string, string> = {
          USER_NOT_FOUND:           'No user account exists for that email. They must sign up first.',
          USER_HAS_LIFETIME:        'User already has Lifetime — no grant needed.',
          USER_ALREADY_ON_PAID_PLAN:'User already has a paid plan. Extend manually if needed.',
          INVALID_MONTHS:           'Months must be between 1 and 24.',
        };
        throw new Error(map[data?.error] || data?.message || data?.error || 'Grant failed.');
      }
      setGrantResult(prev => ({
        ...prev,
        [f.id]: {
          ok: true,
          text: `✓ Granted ${months} months Pro until ${new Date(data.granted_until).toLocaleDateString()}.`,
        },
      }));
      load();
    } catch (err) {
      setGrantResult(prev => ({ ...prev, [f.id]: { ok: false, text: (err as Error).message } }));
    } finally {
      setGrantingId(null);
    }
  };

  const sendReply = async (f: Feedback) => {
    const subject = (replySubject[f.id] || defaultSubject).trim();
    const bodyText = (replyBody[f.id] || '').trim();
    if (!subject || !bodyText) {
      setSendResult(prev => ({ ...prev, [f.id]: { ok: false, text: 'Subject and body are required.' } }));
      return;
    }
    if (!confirm(`Send this reply to ${f.user_email}?`)) return;

    setSendingId(f.id);
    setSendResult(prev => ({ ...prev, [f.id]: { ok: true, text: 'Sending…' } }));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-feedback-reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            feedback_id: f.id,
            subject,
            reply_text: bodyText,
            admin_notes: notesDraft[f.id] || undefined,
          }),
        },
      );
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = (payload && typeof payload.error === 'string' && payload.error)
          || `Send failed (${res.status})`;
        throw new Error(msg);
      }
      setSendResult(prev => ({ ...prev, [f.id]: { ok: true, text: `Reply sent to ${f.user_email}.` } }));
      // Clear the draft, close the row, refresh.
      setReplySubject(prev => ({ ...prev, [f.id]: '' }));
      setReplyBody(prev => ({ ...prev, [f.id]: '' }));
      setTimeout(() => {
        load();
        setExpanded(null);
      }, 600);
    } catch (err) {
      setSendResult(prev => ({ ...prev, [f.id]: { ok: false, text: (err as Error).message } }));
    } finally {
      setSendingId(null);
    }
  };

  const sources = useMemo(() => {
    const s = new Set<string>();
    list.forEach(f => s.add(f.source));
    return Array.from(s);
  }, [list]);

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
            ▸ Growth feedback
          </div>
          <h2 className="text-2xl font-black text-white">
            {unreadCount} unread <span className="text-gray-500 font-normal">/ {totalCount} total</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Replies from broadcasts that included <code className="text-cyan-400">{'{{feedback_url}}'}</code>. Read → reply personally (gmail) → mark responded.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-gray-200 hover:bg-white/5 text-xs"
        >
          <FiRefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div className="mb-4 flex items-center gap-2">
        {(['unread', 'all'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-widest transition-colors ${
              filter === f
                ? 'bg-cyan-500/10 border border-cyan-400/40 text-cyan-300'
                : 'border border-white/10 text-gray-500 hover:text-gray-300'
            }`}
          >
            {f === 'unread' ? `▸ Unread (${unreadCount})` : `All (${totalCount})`}
          </button>
        ))}
        {sources.length > 0 && (
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-gray-500">
            Sources: {sources.join(' · ')}
          </span>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500 font-mono text-xs">
          <FiLoader className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading feedback…
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-white/10 rounded-2xl">
          <FiInbox className="w-8 h-8 text-gray-600 mx-auto mb-3" />
          <p className="text-white font-medium mb-1">
            {filter === 'unread' ? 'Inbox zero.' : 'No feedback yet.'}
          </p>
          <p className="text-xs text-gray-500 font-mono">
            {filter === 'unread'
              ? 'Every reply is marked responded. Try "All" to see history.'
              : 'Broadcasts with {{feedback_url}} will populate this list.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map(f => {
            const isOpen = expanded === f.id;
            const isDone = !!f.responded_at;
            return (
              <div
                key={f.id}
                className={`rounded-2xl border overflow-hidden transition-colors ${
                  isDone
                    ? 'border-white/5 bg-black/20'
                    : 'border-cyan-400/20 bg-cyan-500/[0.03]'
                }`}
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : f.id)}
                  className="w-full text-left p-4 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        {!isDone ? (
                          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                        ) : (
                          <FiCheckCircle className="w-3 h-3 text-green-400" />
                        )}
                        <span className="text-sm text-white font-medium">
                          {f.first_name || f.user_email.split('@')[0]}
                        </span>
                        <span className="text-xs text-gray-500 font-mono truncate">
                          {f.user_email}
                        </span>
                        <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-gray-600 whitespace-nowrap">
                          {new Date(f.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300 line-clamp-2">
                        {f.message}
                      </p>
                    </div>
                  </div>
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border-t border-white/5 bg-black/40"
                    >
                      <div className="p-5 space-y-4">
                        <div>
                          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                            Full message
                          </div>
                          <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed p-4 rounded-lg bg-black/60 border border-white/5">
                            {f.message}
                          </p>
                        </div>

                        {isDone ? (
                          <>
                            <div className="flex items-center gap-2 text-xs text-green-400 font-mono">
                              <FiCheckCircle className="w-3.5 h-3.5" />
                              Marked responded {new Date(f.responded_at!).toLocaleString()}
                            </div>
                            {f.admin_reply_text && (
                              <div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Your reply</div>
                                <p className="text-xs text-gray-300 whitespace-pre-wrap p-3 rounded-lg bg-black/40 border border-white/5">
                                  {f.admin_reply_text}
                                </p>
                              </div>
                            )}
                            {f.pro_granted_until && (
                              <div className="flex items-center gap-2 text-xs font-mono text-green-400 bg-green-500/5 border border-green-500/30 rounded-lg px-3 py-2">
                                <FiCheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                Pro Trader trial granted until {new Date(f.pro_granted_until).toLocaleDateString()}
                                {f.pro_granted_at && (
                                  <span className="text-green-500/60 ml-auto">
                                    on {new Date(f.pro_granted_at).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            )}
                            {!f.pro_granted_until && (
                              <div className="flex items-center gap-2 flex-wrap pt-1">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Still owe them the 3mo?</span>
                                <button
                                  onClick={() => grantPro(f, 3)}
                                  disabled={grantingId === f.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-500/30 text-green-300 hover:bg-green-500/10 text-xs font-medium disabled:opacity-40"
                                >
                                  {grantingId === f.id
                                    ? <FiLoader className="w-3 h-3 animate-spin" />
                                    : <FiCheckCircle className="w-3 h-3" />}
                                  Grant 3mo Pro now
                                </button>
                                {grantResult[f.id] && (
                                  <span className={`text-[11px] font-mono ${
                                    grantResult[f.id].ok ? 'text-green-400' : 'text-red-400'
                                  }`}>
                                    {grantResult[f.id].text}
                                  </span>
                                )}
                              </div>
                            )}
                            {f.admin_notes && (
                              <div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Admin notes</div>
                                <p className="text-xs text-gray-400 whitespace-pre-wrap p-3 rounded-lg bg-black/40 border border-white/5">
                                  {f.admin_notes}
                                </p>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {/* In-app reply composer — sends via send-feedback-reply
                                Edge Function, auto-records on the feedback row, and
                                marks responded on success. */}
                            <div>
                              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-2">
                                ▸ Reply to {f.first_name || f.user_email.split('@')[0]}
                              </div>
                              <input
                                type="text"
                                value={replySubject[f.id] ?? defaultSubject}
                                onChange={e => setReplySubject(prev => ({ ...prev, [f.id]: e.target.value }))}
                                placeholder="Subject"
                                maxLength={200}
                                className="w-full mb-2 bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400/50"
                              />
                              <textarea
                                value={replyBody[f.id] ?? defaultReplyBody(
                                  f.first_name || f.user_email.split('@')[0],
                                  f.message.length > 100 ? f.message.slice(0, 100) + '…' : f.message,
                                )}
                                onChange={e => setReplyBody(prev => ({ ...prev, [f.id]: e.target.value }))}
                                rows={10}
                                placeholder="Write your personal reply here…"
                                className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50 resize-y font-mono leading-relaxed"
                              />
                              <div className="text-[10px] font-mono text-gray-600 mt-1">
                                Sent from <span className="text-gray-400">noreply@snap-chart.ai</span> · reply-to <span className="text-gray-400">jeanmichelportal7@gmail.com</span> · plain text
                              </div>
                            </div>
                            <div>
                              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
                                Admin notes (optional — internal only)
                              </div>
                              <textarea
                                value={notesDraft[f.id] || ''}
                                onChange={e => setNotesDraft({ ...notesDraft, [f.id]: e.target.value })}
                                rows={2}
                                placeholder="e.g. 'granted 3mo Pro via admin' · 'follow up next week'"
                                className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50"
                              />
                            </div>

                            {sendResult[f.id] && (
                              <div className={`px-3 py-2 rounded-lg border font-mono text-xs ${
                                sendResult[f.id].ok
                                  ? 'bg-green-500/5 border-green-500/30 text-green-300'
                                  : 'bg-red-500/5 border-red-500/30 text-red-300'
                              }`}>
                                {sendResult[f.id].ok ? '✓' : '✗'} {sendResult[f.id].text}
                              </div>
                            )}

                            {/* Grant Pro row — separate action so admin can grant
                                before/without emailing, or grant AND send reply
                                (in that order). Status persists to the row. */}
                            <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-white/5">
                              <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mr-1">
                                Fulfill the 3mo Pro promise:
                              </span>
                              <button
                                onClick={() => grantPro(f, 3)}
                                disabled={grantingId === f.id}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-green-500/30 text-green-300 hover:bg-green-500/10 text-xs font-medium disabled:opacity-40"
                              >
                                {grantingId === f.id
                                  ? <FiLoader className="w-3 h-3 animate-spin" />
                                  : <FiCheckCircle className="w-3 h-3" />}
                                Grant 3 months Pro
                              </button>
                              {grantResult[f.id] && (
                                <span className={`text-[11px] font-mono ${
                                  grantResult[f.id].ok ? 'text-green-400' : 'text-red-400'
                                }`}>
                                  {grantResult[f.id].text}
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => setPreviewFor(f)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-cyan-400/30 text-cyan-300 hover:bg-cyan-500/10 text-xs"
                                title="Preview the email exactly as the recipient will see it"
                              >
                                <FiEye className="w-3.5 h-3.5" />
                                Preview
                              </button>
                              <a
                                href={`mailto:${f.user_email}?subject=${encodeURIComponent('Re: your feedback on Snap Chart AI')}&body=${encodeURIComponent(`Hi ${f.first_name || f.user_email.split('@')[0]},\n\nThanks for your feedback. ${f.message.slice(0, 80)}${f.message.length > 80 ? '…' : ''}\n\n`)}`}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-gray-500 hover:text-gray-200 hover:bg-white/5 text-xs"
                                title="Fallback: open in gmail if you'd rather send from your inbox"
                              >
                                <FiMail className="w-3.5 h-3.5" />
                                Open in gmail
                                <FiExternalLink className="w-3 h-3" />
                              </a>
                              <button
                                onClick={() => markResponded(f.id)}
                                disabled={savingId === f.id || sendingId === f.id}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5 text-xs disabled:opacity-40"
                              >
                                {savingId === f.id ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiCheckCircle className="w-3.5 h-3.5" />}
                                Mark as done (no email)
                              </button>
                              <button
                                onClick={() => sendReply(f)}
                                disabled={sendingId === f.id || !(replyBody[f.id] ?? defaultReplyBody(f.first_name || f.user_email.split('@')[0], '')).trim()}
                                className="ml-auto inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-xs font-bold hover:scale-[1.02] transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {sendingId === f.id ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSend className="w-3.5 h-3.5" />}
                                Send reply
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Preview modal ────────────────────────────────────────────
          Renders the reply exactly as the recipient will see it — same
          HTML wrapper as the Edge Function so what you see here is what
          gmail/outlook will paint. */}
      <AnimatePresence>
        {previewFor && (() => {
          const f = previewFor;
          const displayName = f.first_name || f.user_email.split('@')[0];
          const subj = (replySubject[f.id] || defaultSubject).trim() || defaultSubject;
          const bodyText = (replyBody[f.id] ?? defaultReplyBody(displayName,
            f.message.length > 100 ? f.message.slice(0, 100) + '…' : f.message)).trim();
          const bodyHtml = bodyToHtml(bodyText);
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setPreviewFor(null)}
            >
              <motion.div
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header — the gmail-like envelope */}
                <div className="border-b border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-gray-400">Preview · as {f.user_email} will see it</div>
                      <div className="font-bold text-gray-900 text-lg mt-1 truncate">{subj}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        From <span className="font-mono">noreply@snap-chart.ai</span> · Reply-to <span className="font-mono">jeanmichelportal7@gmail.com</span>
                      </div>
                    </div>
                    <button
                      onClick={() => setPreviewFor(null)}
                      className="p-2 rounded-lg text-gray-400 hover:bg-gray-100"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Rendered email body — mirrors wrapEmailHtml() so the
                    preview is byte-accurate with what the recipient sees.
                    ▸ Snap Chart AI header + AI RISK OFFICER pill + white
                    card + Ocean Reef footer, identical to every other
                    Snap Chart AI email. */}
                <div className="p-6 overflow-y-auto flex-1 bg-[#f4f5f7]">
                  <div style={{ maxWidth: 580, margin: '0 auto' }}>
                    {/* Branded header */}
                    <div style={{ textAlign: 'left', margin: '0 0 28px 0', padding: '0 4px' }}>
                      <span style={{ fontSize: 20, fontWeight: 800, color: '#0a0e1a', letterSpacing: '-0.02em', fontFamily: '-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif' }}>
                        Snap Chart AI
                      </span>
                      <span style={{
                        display: 'inline-block', marginLeft: 8,
                        fontFamily: 'ui-monospace,"SF Mono",Menlo,Consolas,monospace',
                        fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.25em',
                        color: '#0891b2', verticalAlign: 'middle',
                      }}>
                        ▸ ai risk officer
                      </span>
                    </div>

                    {/* White card */}
                    <div
                      style={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 16,
                        padding: 36,
                        boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.04)',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                        color: '#0a0e1a',
                        fontSize: 16,
                        lineHeight: 1.65,
                      }}
                    >
                      <div style={{
                        fontFamily: 'ui-monospace,"SF Mono",Menlo,Consolas,monospace',
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.28em',
                        color: '#0891b2',
                        marginBottom: 12,
                        fontWeight: 600,
                      }}>
                        ▸ Reply from JM
                      </div>
                      <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                      <div style={{
                        borderTop: '1px solid #e5e7eb',
                        paddingTop: 14,
                        marginTop: 24,
                        fontSize: 13,
                        color: '#64748b',
                        lineHeight: 1.6,
                      }}>
                        Just hit <strong style={{ color: '#0a0e1a' }}>Reply</strong> if you have more to add — it comes straight to my inbox.
                      </div>
                    </div>

                    {/* Branded footer with address */}
                    <div style={{ margin: '36px 4px 0 4px', paddingTop: 20, borderTop: '1px solid #e5e7eb' }}>
                      <p style={{ color: '#64748b', fontSize: 12, lineHeight: 1.6, margin: 0 }}>
                        <strong style={{ color: '#334155' }}>Snap Chart AI</strong><br />
                        Ocean Reef, Panamá
                      </p>
                      <p style={{ color: '#94a3b8', fontSize: 12, margin: '14px 0 0 0' }}>
                        Privacy · Terms · Support
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t border-gray-200 p-4 flex items-center justify-between gap-3 bg-white">
                  <div className="text-xs text-gray-500">
                    Looks good? <span className="text-gray-700">Close</span> to keep editing, or send it below.
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPreviewFor(null)}
                      className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm hover:bg-gray-50"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => {
                        setPreviewFor(null);
                        sendReply(f);
                      }}
                      disabled={sendingId === f.id}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-500 text-white text-sm font-bold hover:opacity-90 disabled:opacity-40"
                    >
                      {sendingId === f.id ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
                      Send now
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
};

export default GrowthFeedbackPanel;
